import { ChangeEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import TextField from "../../carbon/Form/TextField";
import SelectField, {
  SelectFieldOption,
} from "../../carbon/Form/SelectField";
import DateField from "../../carbon/Form/DateField";
import MultiEmailInput from "../../UI/InputField/MultiEmailInput";
import DeliveryFileField from "./DeliveryFileField";
import { NewDeliveryFormValues } from "./newDeliveryValidationSchema";
import { DeliveryTime } from "../../../types/deliveries.type";
import {
  OfferAwardedForDeliveryResponse,
  SiteResponse,
  UserResponse,
} from "../../../types";
import { TO_BE_AGREED } from "../../../utils";

interface DeliveryFormFieldsProps {
  control: Control<NewDeliveryFormValues>;
  register: UseFormRegister<NewDeliveryFormValues>;
  setValue: UseFormSetValue<NewDeliveryFormValues>;
  errors: FieldErrors<NewDeliveryFormValues>;
  sites?: SiteResponse[];
  awardedSuppliers?: UserResponse[];
  selectedSupplier: OfferAwardedForDeliveryResponse | null;
  selectedOrderId: string;
  // File attach
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName: string | null;
  fileError: string | null;
  isUploading: boolean;
}

/**
 * The field body of the create-delivery form (Carbon migration, Task 6). Every text/select/date
 * field is a shared `src/components/carbon/Form/*` primitive, RHF `Controller`-bound. Two controls
 * are deliberately NOT Carbon primitives and reuse the pre-Carbon components unchanged, per the
 * task's "reuse existing pickers / keep the current upload mechanism" rule: `MultiEmailInput` (the
 * supplier-emails picker, still `register`/`setValue`-driven) and `DeliveryFileField` (the native
 * labelled file input feeding `useFileInputUploader`).
 */
const DeliveryFormFields = ({
  control,
  register,
  setValue,
  errors,
  sites,
  awardedSuppliers,
  selectedSupplier,
  selectedOrderId,
  onFileChange,
  fileName,
  fileError,
  isUploading,
}: DeliveryFormFieldsProps) => {
  const { t } = useTranslation();

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const deliveryTimeOptions: SelectFieldOption[] = [
    { value: DeliveryTime.MORNING, label: t("deliveries.form.morning") },
    { value: DeliveryTime.AFTERNOON, label: t("deliveries.form.afternoon") },
    { value: DeliveryTime.NIGHT, label: t("deliveries.form.night") },
  ];

  const siteOptions: SelectFieldOption[] = [
    ...(sites ?? []).map((site) => ({ value: site.id, label: site.name })),
    { value: TO_BE_AGREED, label: TO_BE_AGREED },
  ];

  return (
    <div className="new-delivery-form__grid">
      <TextField
        name="orderName"
        label={t("deliveries.form.orderName")}
        control={control}
      />
      <TextField
        name="purchaseOrderNumber"
        label={t("deliveries.form.purchaseOrderNumber")}
        control={control}
      />

      <div className="new-delivery-form__cell">
        <MultiEmailInput
          id="supplierEmails"
          name="supplierEmails"
          label={t("deliveries.form.emails")}
          errorName={errors?.supplierEmails}
          errorMessage={errors?.supplierEmails?.message}
          required={!!selectedSupplier}
          register={register}
          setValue={setValue}
          value={
            selectedSupplier
              ? [selectedSupplier.supplier.email]
              : awardedSuppliers && selectedOrderId
                ? awardedSuppliers.map((supplier) => supplier.email)
                : []
          }
          disabled={false}
          key={
            selectedOrderId +
            (selectedSupplier ? selectedSupplier.supplier.id : "")
          }
        />
        {awardedSuppliers &&
          awardedSuppliers.length > 0 &&
          !selectedSupplier && (
            <p className="new-delivery-form__hint">
              {t("deliveries.form.awardedSuppliers")}:{" "}
              {awardedSuppliers
                .map((supplier) => supplier.company?.name)
                .join(", ")}
            </p>
          )}
      </div>

      <div className="new-delivery-form__cell">
        <TextField
          name="receiverEmail"
          label={t("deliveries.form.receiverEmail")}
          type="email"
          control={control}
        />
        <p className="new-delivery-form__hint">
          {t("deliveries.form.receiverEmailHelp")}
        </p>
      </div>

      <div className="new-delivery-form__triple">
        <DateField
          name="deliveryDate"
          label={t("deliveries.form.deliveryDate")}
          control={control}
          minDate={today}
        />
        <SelectField
          name="deliveryTime"
          label={t("deliveries.form.deliveryTime")}
          control={control}
          options={deliveryTimeOptions}
          placeholder={t("deliveries.form.selectTimeRange")}
        />
        <SelectField
          name="siteId"
          label={t("newMaterialForm.deliveryPlace")}
          control={control}
          options={siteOptions}
          placeholder={t("newMaterialForm.selectDeliveryPlace")}
        />
      </div>

      <div className="new-delivery-form__full">
        <DeliveryFileField
          register={register}
          onChange={onFileChange}
          fileName={fileName}
          fileError={fileError}
          isUploading={isUploading}
        />
      </div>

      <div className="new-delivery-form__full">
        <TextField
          name="comment"
          label={t("deliveries.form.comment")}
          control={control}
        />
      </div>
    </div>
  );
};

export default DeliveryFormFields;
